/**
 * LocalConnection — single-player GameConnection. A dedicated Web Worker (sp.worker.ts) runs the
 * GameSession and speaks the same ServerMessage protocol as the multiplayer server, via
 * postMessage (structured clone keeps typed arrays intact), so the rest of the client cannot
 * tell the difference. Closing the connection terminates the worker and with it the match.
 */
import type { ForcedWeather, SessionConfig } from '@friction/engine';
import type { ClientMessage, GameSpeed, ServerMessage } from '@friction/protocol';
import type { ConnectionStatus, GameConnection } from './connection';

/** Messages the main thread sends to the single-player worker. */
export type SpWorkerRequest =
  | { t: 'spInit'; config: SessionConfig; speed: GameSpeed; paused?: boolean; devWeather?: ForcedWeather }
  | ClientMessage;

function spawnWorker(): Worker {
  // Literal `new Worker(new URL(...), { type: 'module' })` so Vite bundles the worker.
  return new Worker(new URL('./sp.worker.ts', import.meta.url), { type: 'module', name: 'friction-sp' });
}

export class LocalConnection implements GameConnection {
  readonly kind = 'local' as const;
  private worker: Worker | null;
  private _status: ConnectionStatus = 'connecting';
  private readonly msgListeners = new Set<(msg: ServerMessage) => void>();
  private readonly statusListeners = new Set<(status: ConnectionStatus) => void>();

  constructor(
    config: SessionConfig,
    speed: GameSpeed,
    createWorker: () => Worker = spawnWorker,
    /** Start with the clock stopped (the Kriegsakademie opens on a paused map). */
    paused = false,
    /** Dev weather checks: pin the sky. */
    devWeather?: ForcedWeather,
  ) {
    const worker = createWorker();
    this.worker = worker;
    worker.onmessage = (e: MessageEvent<ServerMessage>) => {
      if (this._status === 'connecting') this.setStatus('open');
      this.emit(e.data);
    };
    worker.onerror = (e: ErrorEvent) => {
      e.preventDefault();
      this.emit({ t: 'error', code: 'internal', message: `The single-player engine crashed: ${e.message}` });
      this.close();
    };
    worker.onmessageerror = () => {
      this.emit({
        t: 'error',
        code: 'internal',
        message: 'The single-player engine sent an unreadable message.',
      });
    };
    worker.postMessage({ t: 'spInit', config, speed, paused, devWeather } satisfies SpWorkerRequest);
  }

  get status(): ConnectionStatus {
    return this._status;
  }

  send(msg: ClientMessage): void {
    this.worker?.postMessage(msg satisfies SpWorkerRequest);
  }

  onMessage(fn: (msg: ServerMessage) => void): () => void {
    this.msgListeners.add(fn);
    return () => this.msgListeners.delete(fn);
  }

  onStatus(fn: (status: ConnectionStatus) => void): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  close(): void {
    if (!this.worker) return;
    this.worker.terminate();
    this.worker = null;
    this.setStatus('closed');
  }

  private emit(msg: ServerMessage): void {
    for (const fn of this.msgListeners) {
      try {
        fn(msg);
      } catch (err) {
        console.error(`[net] handler for "${msg.t}" failed`, err);
      }
    }
  }

  private setStatus(status: ConnectionStatus): void {
    if (status === this._status) return;
    this._status = status;
    for (const fn of this.statusListeners) fn(status);
  }
}
